import { useId, useState, type FormEvent } from "react";
import type { Contact, ContactInput } from "@/api/clients";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export const INVOICE_RECIPIENT_EMAIL = "An e-mail address is required to receive invoices.";

type ContactFormProps = {
  initial?: Contact | null;
  submitLabel?: string;
  onSubmit: (input: ContactInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function ContactForm({
  initial,
  submitLabel = "Save contact",
  onSubmit,
  onCancel,
  onReload,
}: ContactFormProps) {
  const id = useId();
  const { values, set } = useFormValues<ContactInput>({
    name: initial?.name ?? "",
    title: initial?.title ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    receives_invoices: initial?.receives_invoices ?? false,
    is_primary: initial?.is_primary ?? false,
  });
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const { error, errors: apiErrors, saving, run } = useSubmitState();
  const errors = { ...apiErrors, ...localErrors };

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const input: ContactInput = {
      ...values,
      name: values.name.trim(),
      title: values.title.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
    };
    if (input.receives_invoices && !input.email) {
      setLocalErrors({ email: INVOICE_RECIPIENT_EMAIL });
      return;
    }
    setLocalErrors({});
    void run(() => onSubmit(input));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-name`}
          label="Name"
          value={values.name}
          onChange={(value) => set("name", value)}
          error={errors.name}
        />
        <TextField
          id={`${id}-title`}
          label="Title"
          value={values.title}
          onChange={(value) => set("title", value)}
          error={errors.title}
          hint="e.g. Operations, Finance"
        />
        <TextField
          id={`${id}-email`}
          label="E-mail"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={(value) => set("email", value)}
          error={errors.email}
          hint={values.receives_invoices ? INVOICE_RECIPIENT_EMAIL : undefined}
        />
        <TextField
          id={`${id}-phone`}
          label="Phone"
          type="tel"
          value={values.phone}
          onChange={(value) => set("phone", value)}
          error={errors.phone}
        />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
        <Checkbox
          id={`${id}-invoices`}
          label="Receives invoices"
          checked={values.receives_invoices}
          onChange={(checked) => set("receives_invoices", checked)}
        />
        <Checkbox
          id={`${id}-primary`}
          label="Primary contact"
          checked={values.is_primary}
          onChange={(checked) => set("is_primary", checked)}
        />
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
